import { AlertTriangle, RotateCw } from "lucide-react";
import React from "react";
import { Navigate } from "react-router-dom";

import {
  PARTNER_ACCESS_ERROR_DESCRIPTION,
  PARTNER_ACCESS_ERROR_TITLE,
  PARTNER_RETRY_LABEL,
} from "constants/partner/partnerCopy";
import { DASHBOARD_PATH } from "constants/partner/partnerRoutes";
import { usePartnerAccess } from "hooks/usePartnerAccess";

import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";

/**
 * Route guard that only renders children for referral partners.
 * Shows nothing while the partner lookup is loading, an error state with a
 * retry when the lookup failed, and redirects confirmed non-partners.
 */
const PartnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isPartner, isLoading, loadError, refreshAsync } = usePartnerAccess();

  if (isLoading) {
    return null;
  }

  if (loadError) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="destructive" className="flex flex-col gap-4">
          <div>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{PARTNER_ACCESS_ERROR_TITLE}</AlertTitle>
            <AlertDescription>{PARTNER_ACCESS_ERROR_DESCRIPTION}</AlertDescription>
          </div>
          <Button variant="outline" className="self-start" onClick={() => void refreshAsync()}>
            <RotateCw className="mr-2 h-4 w-4" />
            {PARTNER_RETRY_LABEL}
          </Button>
        </Alert>
      </div>
    );
  }

  if (!isPartner) {
    return <Navigate to={DASHBOARD_PATH} replace />;
  }

  return <>{children}</>;
};

export { PartnerRoute };
